import React, { useContext, useEffect, useState } from "react";
import AppContext from "../../contexts/AppContext";
import { Line, Pie } from "react-chartjs-2";
import {
    ArcElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import moment from "moment";
import { FaChevronDown, FaRegCalendarAlt } from "react-icons/fa";
import { hasElevatedSessionAccess } from "../../utils/sessionPermissions";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

const PIE_COLORS = [
    "#2A3E59",
    "#54728C",
    "#F2D98D",
    "#A6786D",
    "#D9665B",
    "#898C1F",
    "#3A403A",
];

const getYesterday = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
};

const getInitialStartDate = (endDate) => {
    const startDate = new Date(endDate);
    startDate.setDate(endDate.getDate() - 6);
    return startDate;
};

const formatDate = (date) => date.toISOString().split("T")[0];
const normalizeSpotDetails = (entry) => (Array.isArray(entry?.spotDetails) ? entry.spotDetails : []);
const normalizeRecordCount = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : 0;
};

const AdvLogAnalysis = () => {
    const { selectedRole, token, userInfo } = useContext(AppContext);
    const [advLogData, setAdvLogData] = useState([]);
    const [selectedSpotId, setSelectedSpotId] = useState("all");
    const [loading, setLoading] = useState(false);

    const yesterday = getYesterday();
    const [startDate, setStartDate] = useState(() => getInitialStartDate(yesterday));
    const [endDate, setEndDate] = useState(() => yesterday);
    const [timeRange, setTimeRange] = useState("7");
    const [maxTicks, setMaxTicks] = useState(window.innerWidth < 640 ? 4 : 15);

    const roleArray = userInfo?.roleInfo?.[selectedRole?.type] || [];
    const currentRole = roleArray.find((r) => r.id === selectedRole?.id);
    const permissionLevel = currentRole?.permissionLevel;
    const canShowAdvanced = hasElevatedSessionAccess(userInfo, permissionLevel);

    useEffect(() => {
        const handleResize = () => {
            setMaxTicks(window.innerWidth < 640 ? 4 : 20);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!canShowAdvanced && ["90", "180", "365", "custom"].includes(timeRange)) {
            const newEndDate = getYesterday();
            const newStartDate = new Date(newEndDate);
            newStartDate.setMonth(newEndDate.getMonth() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
            setTimeRange("30");
            setStartDate(newStartDate);
            setEndDate(newEndDate);
        }
    }, [canShowAdvanced, timeRange]);

    const handleTimeRangeChange = (e) => {
        const range = e.target.value;
        setTimeRange(range);

        const newEndDate = getYesterday();
        const newStartDate = new Date(newEndDate);

        if (range === "7") {
            newStartDate.setDate(newEndDate.getDate() - 6);
        } else if (range === "14") {
            newStartDate.setDate(newEndDate.getDate() - 13);
        } else if (range === "30") {
            newStartDate.setMonth(newEndDate.getMonth() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "90") {
            newStartDate.setMonth(newEndDate.getMonth() - 3);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "180") {
            newStartDate.setMonth(newEndDate.getMonth() - 6);
            newStartDate.setDate(newStartDate.getDate() + 1);
        } else if (range === "365") {
            newStartDate.setFullYear(newEndDate.getFullYear() - 1);
            newStartDate.setDate(newStartDate.getDate() + 1);
        }

        setStartDate(newStartDate);
        setEndDate(newEndDate);
    };

    const calcStepSize = (values) => {
        if (!values?.length) return undefined;

        const maxVal = Math.max(...values);
        if (maxVal <= 10) return 1;
        if (maxVal <= 50) return 5;
        if (maxVal <= 100) return 10;
        if (maxVal <= 500) return 50;
        if (maxVal <= 1000) return 100;
        if (maxVal <= 5000) return 500;
        return undefined;
    };

    const handleStartDateChange = (date) => {
        if (date > endDate) {
            setEndDate(date);
        }
        setStartDate(new Date(date.setHours(0, 0, 0, 0)));
        setTimeRange("custom");
    };

    const handleEndDateChange = (date) => {
        if (date < startDate) {
            setStartDate(date);
        }
        setEndDate(new Date(date.setHours(23, 59, 59, 999)));
        setTimeRange("custom");
    };

    useEffect(() => {
        // Set when another advertiser or period is chosen: a late answer for the old choice must not replace the new one.
        let outdated = false;
        const fetchAdvLogData = async () => {
            if (!selectedRole?.id || selectedRole?.type !== "adv") {
                setAdvLogData([]);
                return;
            }

            try {
                setLoading(true);
                const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const localStart = new Date(startDate);
                const localEnd = new Date(endDate);
                localStart.setHours(0, 0, 0, 0);
                localEnd.setHours(23, 59, 59, 999);

                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/analysis/advertisement/log/summary/read`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        adv_id: selectedRole.id,
                        startDate: dayjs(localStart).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        endDate: dayjs(localEnd).format("YYYY-MM-DDTHH:mm:ss.SSS"),
                        userTimeZone,
                    }),
                });

                const data = await response.json();
                if (outdated) return;
                if (!response.ok) {
                    console.error("Failed to fetch advertisement analytics:", data?.error || response.statusText);
                    setAdvLogData([]);
                    return;
                }

                setAdvLogData(Array.isArray(data) ? data : []);
            } catch (error) {
                if (outdated) return;
                console.error("Failed to fetch data:", error);
                setAdvLogData([]);
            } finally {
                if (!outdated) setLoading(false);
            }
        };

        fetchAdvLogData();
        return () => { outdated = true; };
    }, [endDate, selectedRole, startDate, token]);

    useEffect(() => {
        setSelectedSpotId("all");
    }, [selectedRole?.id]);

    const handleSpotChange = (e) => {
        setSelectedSpotId(e.target.value);
    };

    const generateDateRange = (start, end) => {
        const current = new Date(start);
        const last = new Date(end);
        const dateList = [];

        while (current <= last) {
            dateList.push(formatDate(current));
            current.setDate(current.getDate() + 1);
        }

        return dateList;
    };

    const uniqueSpots = Array.from(
        advLogData
            .flatMap((entry) =>
                normalizeSpotDetails(entry).map((spot) => ({
                    spotId: spot.venue_id,
                    spotName: spot.venueName,
                }))
            )
            .reduce((map, spot) => map.set(spot.spotId, spot.spotName), new Map())
    );

    const calculateTotals = () => {
        const dateRange = generateDateRange(startDate, endDate);

        return dateRange.map((date) => {
            const entry = advLogData.find((item) => item.date === date);
            return {
                date,
                totalRecords: entry
                    ? normalizeSpotDetails(entry).reduce((sum, spot) => sum + normalizeRecordCount(spot.totalRecords), 0)
                    : 0,
            };
        });
    };

    const calculateSpotTotals = () => {
        const dateRange = generateDateRange(startDate, endDate);

        if (selectedSpotId === "all") {
            const hotelData = {};

            advLogData.forEach((entry) => {
                normalizeSpotDetails(entry).forEach((spot) => {
                    if (!hotelData[spot.venue_id]) {
                        hotelData[spot.venue_id] = {
                            name: spot.venueName,
                            data: [],
                        };
                    }

                    hotelData[spot.venue_id].data.push({
                        date: entry.date,
                        totalRecords: normalizeRecordCount(spot.totalRecords),
                    });
                });
            });

            return Object.entries(hotelData).map(([spotId, details]) => ({
                spotId,
                spotName: details.name,
                data: dateRange.map((date) => {
                    const record = details.data.find((item) => item.date === date);
                    return {
                        date,
                        totalRecords: record ? record.totalRecords : 0,
                    };
                }),
            }));
        }

        return dateRange.map((date) => {
            const entry = advLogData.find((item) => item.date === date);
            const spotDetails = normalizeSpotDetails(entry).find((spot) => spot.venue_id === selectedSpotId);
            return {
                date,
                totalRecords: spotDetails ? normalizeRecordCount(spotDetails.totalRecords) : 0,
            };
        });
    };

    const calculatePageAndContactRatios = () => {
        const filteredData = advLogData.filter(
            (entry) =>
                selectedSpotId === "all" || normalizeSpotDetails(entry).some((spot) => spot.venue_id === selectedSpotId)
        );

        const pageSummaryTotals = {};
        const contactSummaryTotals = {};

        filteredData.forEach((entry) => {
            normalizeSpotDetails(entry)
                .filter((spot) => selectedSpotId === "all" || spot.venue_id === selectedSpotId)
                .forEach((spot) => {
                    Object.entries(spot.pageSummary || {}).forEach(([key, value]) => {
                        pageSummaryTotals[key] = (pageSummaryTotals[key] || 0) + normalizeRecordCount(value);
                    });
                    Object.entries(spot.contactSummary || {}).forEach(([key, value]) => {
                        contactSummaryTotals[key] = (contactSummaryTotals[key] || 0) + normalizeRecordCount(value);
                    });
                });
        });

        const totalPageRecords = Object.values(pageSummaryTotals).reduce((sum, val) => sum + val, 0);
        const totalContactRecords = Object.values(contactSummaryTotals).reduce((sum, val) => sum + val, 0);

        const pageRatios = Object.entries(pageSummaryTotals).map(([key, value]) => ({
            type: key,
            ratio: totalPageRecords ? ((value / totalPageRecords) * 100).toFixed(2) : "0.00",
            value,
        }));

        const contactRatios = Object.entries(contactSummaryTotals).map(([key, value]) => ({
            type: key,
            ratio: totalContactRecords ? ((value / totalContactRecords) * 100).toFixed(2) : "0.00",
            value,
        }));

        return { pageRatios, contactRatios };
    };

    const totals = calculateTotals();
    const totalRecordValues = totals.map((item) => item.totalRecords);
    const selectedSpotName = uniqueSpots.find(([id]) => id === selectedSpotId)?.[1] || "All Spots";
    const spotTotals = calculateSpotTotals();

    const totalRecordsLineData = {
        labels: totals.map((item) => item.date),
        datasets: [
            {
                label: "Total Records",
                data: totalRecordValues,
                borderColor: "rgba(75,192,192,1)",
                backgroundColor: "rgba(75,192,192,0.2)",
            },
        ],
    };

    const filteredSpotTotalsData = {
        labels: generateDateRange(startDate, endDate),
        datasets:
            selectedSpotId === "all"
                ? spotTotals.map((spot) => ({
                    label: spot.spotName,
                    data: spot.data.map((entry) => entry.totalRecords),
                    borderColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(
                        Math.random() * 255
                    )}, 1)`,
                    backgroundColor: "rgba(0,0,0,0)",
                }))
                : [
                    {
                        label: `Total Records (${selectedSpotName})`,
                        data: spotTotals.map((item) => item.totalRecords),
                        borderColor: "rgba(255,99,132,1)",
                        backgroundColor: "rgba(255,99,132,0.2)",
                    },
                ],
    };

    const filteredSpotValues =
        selectedSpotId === "all"
            ? filteredSpotTotalsData.datasets.flatMap((dataset) => dataset.data)
            : filteredSpotTotalsData.datasets[0]?.data || [];

    const { pageRatios, contactRatios } = calculatePageAndContactRatios();

    const renderPieChart = (ratios, title) => {
        const labels = ratios.map((item) => item.type);
        const values = ratios.map((item) => item.value);
        const ratiosPercent = ratios.map((item) => item.ratio);

        return (
            <div className="w-full h-[320px] mb-4">
                <h4 className="flex font-semibold justify-center mb-8">{title}</h4>
                <div className="flex h-[300px] justify-start items-center">
                    {loading ? (
                        <p className="text-lg font-semibold text-gray-500">Loading...</p>
                    ) : (
                        <Pie
                            data={{
                                labels,
                                datasets: [
                                    {
                                        data: values,
                                        backgroundColor: PIE_COLORS,
                                    },
                                ],
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: "left",
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label(context) {
                                                const index = context.dataIndex;
                                                const label = context.chart.data.labels[index];
                                                const value = context.chart.data.datasets[0].data[index];
                                                const ratio = ratiosPercent[index];
                                                return `${label}: ${value} (${ratio}%)`;
                                            },
                                        },
                                    },
                                    datalabels: {
                                        color: "#fff",
                                        formatter(value, context) {
                                            return `${ratiosPercent[context.dataIndex]}%`;
                                        },
                                    },
                                },
                            }}
                        />
                    )}
                </div>
            </div>
        );
    };

    const optionsForLineChart = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
            legend: {
                display: false,
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Time (Date)",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
                ticks: {
                    maxTicksLimit: maxTicks,
                    callback(value) {
                        const rawDateStr = this.getLabelForValue(value);
                        const dateObj = moment(rawDateStr, "YYYY-MM-DD");
                        return dateObj.format("MMM D");
                    },
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: calcStepSize(totalRecordValues),
                },
                title: {
                    display: true,
                    text: "Click counts",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
            },
        },
    };

    const optionsForMutiLine = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            datalabels: {
                display: false,
            },
            legend: {
                onClick: () => {},
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Time (Date)",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
                ticks: {
                    maxTicksLimit: maxTicks,
                    callback(value) {
                        const rawDateStr = this.getLabelForValue(value);
                        const dateObj = moment(rawDateStr, "YYYY-MM-DD");
                        return dateObj.format("MMM D");
                    },
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: calcStepSize(filteredSpotValues),
                },
                title: {
                    display: true,
                    text: "Click counts",
                    font: {
                        size: 14,
                        weight: "bold",
                    },
                    color: "#4B5563",
                },
            },
        },
    };

    return (
        <div className=" p-6 bg-gray-50 rounded-lg shadow-lg">
            <div className="mb-4 flex justify-between items-center">
                <div className="flex gap-4 items-center">
                    <label className="block">
                        <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                            Time Range
                        </span>
                        <div className="relative mt-2 inline-flex items-center">
                            <select
                                value={timeRange}
                                onChange={handleTimeRangeChange}
                                className="h-11 w-44 appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-4 pr-10 text-[15px] font-medium text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                            >
                                <option value="7">Past 7 Days</option>
                                <option value="14">Past 14 Days</option>
                                <option value="30">Past 30 Days</option>
                                {canShowAdvanced && (
                                    <>
                                        <option value="90">Past 3 Months</option>
                                        <option value="180">Past 6 Months</option>
                                        <option value="365">Past 12 Months</option>
                                        <option value="custom">Customrize</option>
                                    </>
                                )}
                            </select>
                            <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff]">
                                <FaChevronDown className="h-3 w-3" />
                            </span>
                        </div>
                    </label>

                    {timeRange === "custom" && canShowAdvanced && (
                        <>
                            <label className="block">
                                <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                                    Start Date
                                </span>
                                <div className="relative mt-2 inline-flex items-center">
                                    <DatePicker
                                        selected={startDate}
                                        onChange={handleStartDateChange}
                                        maxDate={getYesterday()}
                                        className="h-11 w-44 rounded-xl border border-[#dbe3f5] bg-white pl-10 pr-3 text-center text-[14px] text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                                    />
                                    <span className="pointer-events-none absolute left-3 text-[#8a97b4]">
                                        <FaRegCalendarAlt className="h-4 w-4" />
                                    </span>
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                                    End Date
                                </span>
                                <div className="relative mt-2 inline-flex items-center">
                                    <DatePicker
                                        selected={endDate}
                                        onChange={handleEndDateChange}
                                        minDate={startDate}
                                        maxDate={getYesterday()}
                                        className="h-11 w-44 rounded-xl border border-[#dbe3f5] bg-white pl-10 pr-3 text-center text-[14px] text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                                    />
                                    <span className="pointer-events-none absolute left-3 text-[#8a97b4]">
                                        <FaRegCalendarAlt className="h-4 w-4" />
                                    </span>
                                </div>
                            </label>
                        </>
                    )}
                </div>
            </div>

            <div className="h-[300px] mb-4">
                <h3 className="text-center font-semibold">Total Advertisement Clicks
                    <span className="ml-2 relative group cursor-pointer">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-sm font-bold">
                            ?
                        </span>
                        <div className="absolute hidden group-hover:block bg-white text-gray-700 text-sm p-2 rounded shadow-md whitespace-normal w-[400px] -top-10 left-3/4 transform translate-x-[10%] text-justify">
                            This chart shows the total number of clicks on your advertisement across all the venues.
                        </div>
                    </span>
                </h3>
                <Line data={totalRecordsLineData} options={optionsForLineChart} />
            </div>

            <div className="flex gap-4 items-center mt-10 mb-10">
                <label className="block">
                    <span className="text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]">
                        Select Spot
                    </span>
                    <div className="relative mt-2 inline-flex items-center">
                        <select
                            value={selectedSpotId}
                            onChange={handleSpotChange}
                            className="h-11 w-52 appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-4 pr-10 text-[15px] font-medium text-[#24324d] shadow-none outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
                        >
                            <option value="all">All Spots</option>
                            {uniqueSpots.map(([spotId, spotName]) => (
                                <option key={spotId} value={spotId}>
                                    {spotName}
                                </option>
                            ))}
                        </select>
                        <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff]">
                            <FaChevronDown className="h-3 w-3" />
                        </span>
                    </div>
                </label>
            </div>

            <div className="h-[300px] mb-4">
                <h3 className="text-center font-semibold">Daily Venue Clicks
                    <span className="ml-2 relative group cursor-pointer">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-300 text-gray-700 text-sm font-bold">
                            ?
                        </span>
                        <div className="absolute hidden group-hover:block bg-white text-gray-700 text-sm p-2 rounded shadow-md whitespace-normal w-[400px] -top-10 left-3/4 transform translate-x-[10%] text-justify">
                            This chart shows the daily click trends of advertisements for each venue, helping you analyze the performance across different venues.
                            And you can use the buttons on the left to switch between different Venues
                        </div>
                    </span>
                </h3>
                {loading ? (
                    <p className="text-lg font-semibold text-gray-500">Loading...</p>
                ) : (
                    <Line data={filteredSpotTotalsData} options={optionsForMutiLine} />
                )}
            </div>

            <div className="flex flex-col md:flex-row gap-4 mt-10">
                {renderPieChart(pageRatios, "Page Click Ratios")}
                {renderPieChart(contactRatios, "Contact Click Ratios")}
            </div>
        </div>
    );
};

export default AdvLogAnalysis;
